import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../Auth/auth.service';
import {
  EMAIL_FALLBACK_USER_MESSAGE,
  EMAIL_INACTIVE_USER_MESSAGE,
  buildSyntheticEmailSendResponse,
  logEmailSendHttpFailure,
} from '../../http/email-send-error.util';
import type { EmailSendApiResponse } from '../../http/email-send.types';

export type { EmailSendApiResponse, EmailFailureKind } from '../../http/email-send.types';

/**
 * User-facing delivery line for successful HTTP flows where the API reports email not sent.
 * Never interpolates raw third-party or backend payloads.
 */
export function formatEmailDeliveryUserMessage(
  res: EmailSendApiResponse,
  context: 'submission' | 'notification' = 'notification'
): string {
  if (res.emailSent) {
    return '';
  }

  const inactive =
    res.emailFailureKind === 'inactive' ||
    (res.emailWarning?.toLowerCase().includes('inactive') ?? false);

  if (context === 'submission') {
    if (inactive) {
      return `Submission successful, but we couldn't send a notification email because the recipient address is inactive.`;
    }
    return 'Submission successful, but notification email could not be delivered.';
  }

  if (inactive) {
    return EMAIL_INACTIVE_USER_MESSAGE;
  }
  return EMAIL_FALLBACK_USER_MESSAGE;
}

function safeMessageFromErrorBody(body: unknown): string | null {
  if (body == null) {
    return null;
  }
  if (typeof body === 'object' && !Array.isArray(body)) {
    const o = body as Record<string, unknown>;
    for (const key of ['message', 'error', 'detail', 'title'] as const) {
      const v = o[key];
      if (typeof v === 'string') {
        const t = v.trim();
        if (t && !t.startsWith('{') && t.length <= 400) {
          return t;
        }
      }
    }
    if (Array.isArray(o['errors']) && o['errors'].length > 0) {
      const first = o['errors'][0];
      if (typeof first === 'string' && first.trim()) {
        return first.trim();
      }
    }
    return null;
  }
  if (typeof body === 'string') {
    const t = body.trim();
    if (!t) {
      return null;
    }
    if (t.startsWith('{')) {
      try {
        const j = JSON.parse(t) as Record<string, unknown>;
        const msg = j['message'] ?? j['Message'] ?? j['error'] ?? j['detail'];
        if (typeof msg === 'string' && msg.trim()) {
          return msg.trim();
        }
      } catch {
        return null;
      }
      return null;
    }
    if (t.length <= 400) {
      return t;
    }
  }
  return null;
}

/** Best-effort message for real API failures; avoids dumping raw JSON blobs into the UI. */
export function describeHttpError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    const fromBody = safeMessageFromErrorBody(error.error);
    if (fromBody) {
      return fromBody;
    }
    if (error.message?.trim()) {
      return error.message.trim();
    }
  }
  if (error instanceof Error && error.message?.trim()) {
    return error.message.trim();
  }
  return 'Request failed. Please try again.';
}

@Injectable({
  providedIn: 'root'

})
export class NotificationService {

  constructor(private http: HttpClient, private authService: AuthService) { }
  private baseUrl = environment.apiBaseUrl;

  sendEmailNotification(filename: string, subject: string, receipientEmails: string): Observable<EmailSendApiResponse> {
    console.log("[sendEmailNotification started..] Receipient Emails:", receipientEmails);

    return this.http.get('assets/email-templates/' + filename + '.html', { responseType: 'text' }).pipe(
      switchMap((htmlBody: string) =>
        this.authService.handleTokenExpiration().pipe(
          switchMap((accessToken: string) => {
            const payload = {
              from: "no_reply_vendorportal@unipet.co",
              to: receipientEmails,
              bcc: "",
              subject: subject,
              textBody: " ",
              htmlBody: htmlBody
            };

            const headers = new HttpHeaders({
              'Authorization': `Bearer ${accessToken}`,
            });

            return this.http.post<EmailSendApiResponse>(`${this.baseUrl}/email/send`, payload, { headers }).pipe(
              catchError((error) => {
                logEmailSendHttpFailure(error, 'NotificationService.sendEmailNotification');
                return of(buildSyntheticEmailSendResponse(error));
              })
            );
          })
        )
      ),
      catchError((error) => {
        console.error('Email notification pipeline failed (e.g. template or auth)', error);
        return throwError(() => error);
      })
    );
  }


  sendNotification(payload: {
    companyId: number;
    adminId: number;
    message: string;
    type: string;
    createdAt: Date;
    fieldNames: string[];

  }) {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        console.log(payload)
        return this.http.post(`${this.baseUrl}/notifications`, payload, { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }

  sendNewSubmissionNotification(payload: {
    companyId: number;
    adminId: number;
    message: string;
    type: string;
    createdAt: Date;
    fieldNames: string[];
  }): Observable<any> {
    console.log('Sending payload to public notification endpoint:', payload);

    return this.http.post(`${this.baseUrl}/notifications/save/public`, payload).pipe(
      catchError((error) => {
        console.error('Failed to send public notification:', error);
        return throwError(() => error);
      })
    );
  }



  getNotifications(companyId: number) {
    console.log(companyId);
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.get<any[]>(`${this.baseUrl}/notifications/${companyId}/notifications`, { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }

  getNotificationsForUser(userId: number): Observable<any[]> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.get<any[]>(`${this.baseUrl}/notifications/${userId}`, { headers: headers });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }

  markAllAsRead(userId: number): Observable<void> {
    return this.authService.handleTokenExpiration().pipe(
      switchMap((accessToken: string) => {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`,
        });
        return this.http.put<void>(`${this.baseUrl}/notifications/mark-all-read/${userId}`, null, {
          responseType: 'text' as 'json', headers
        });
      }),
      catchError((error) => {
        console.error('Failed', error);
        return throwError(() => error);
      })
    );
  }



}
